import { useAuth } from "@/contexts/AuthContext";
import { Navigate, NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import Logo from "@/components/common/Logo";
import {
  LayoutDashboard, Building2, Server, Gamepad2, Wallet, Activity, FlaskConical,
  GitCompare, Banknote, MonitorCheck, Shield, FileText, Image, Users2, BookOpen, LogOut, ChevronRight,
  Landmark,
} from "lucide-react";

const NAV = [
  { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
  { section: "Manage" },
  { to: "/operators", icon: Building2, label: "Operators" },
  { to: "/providers", icon: Server, label: "Providers" },
  { to: "/games", icon: Gamepad2, label: "Game Catalog" },
  { section: "Wallet" },
  { to: "/operator-credits", icon: Landmark, label: "Operator Credits" },
  { to: "/wallet", icon: Wallet, label: "Wallet Ledger" },
  { to: "/sessions", icon: Activity, label: "Sessions" },
  { section: "Operations" },
  { to: "/sandbox", icon: FlaskConical, label: "Sandbox" },
  { to: "/reconciliation", icon: GitCompare, label: "Reconciliation" },
  { to: "/settlement", icon: Banknote, label: "Settlement" },
  { to: "/webhooks", icon: Activity, label: "Webhook Logs" },
  { section: "Platform" },
  { to: "/monitoring", icon: MonitorCheck, label: "Monitoring" },
  { to: "/audit", icon: Shield, label: "Audit Logs" },
  { to: "/ekyc", icon: Users2, label: "eKYC" },
  { to: "/media", icon: Image, label: "Media" },
];

export default function AdminLayout() {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-[var(--text-2)]">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;

  return (
    <div className="min-h-screen flex bg-[var(--bg)] grain">
      {/* Sidebar */}
      <aside className="w-72 border-r border-[var(--border)] flex flex-col" data-testid="admin-sidebar">
        <div className="p-5 border-b border-[var(--border)]">
          <div className="flex items-center gap-2.5">
            <Logo size={32}/>
            <div className="leading-none">
              <div className="font-semibold text-sm tracking-tight">CoreBridge</div>
              <div className="text-[9px] text-[var(--text-3)] uppercase tracking-[0.18em] mt-0.5">Technologies</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-0.5">
          {NAV.map((item, i) => item.section ? (
            <div key={i} className="text-[10px] uppercase tracking-widest text-[var(--text-3)] px-3 mt-4 mb-1">
              {item.section}
            </div>
          ) : (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <item.icon size={16} />
              <span>{item.label}</span>
            </NavLink>
          ))}
          <div className="text-[10px] uppercase tracking-widest text-[var(--text-3)] px-3 mt-4 mb-1">Developer</div>
          <NavLink to="/docs" className="nav-link" data-testid="nav-docs">
            <BookOpen size={16}/> Documentation
            <ChevronRight size={14} className="ml-auto"/>
          </NavLink>
        </nav>
        <div className="border-t border-[var(--border)] p-3">
          <div className="text-xs text-[var(--text-2)] mb-2 truncate" data-testid="current-user-email">
            {user?.email}
          </div>
          <div className="text-[10px] uppercase tracking-widest text-[var(--gold)] mb-3">{user?.role}</div>
          <button
            onClick={async () => { await logout(); nav("/login"); }}
            className="btn-ghost w-full py-2 rounded-md text-xs flex items-center justify-center gap-2"
            data-testid="logout-btn"
          >
            <LogOut size={14}/> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-y-auto">
        <header className="border-b border-[var(--border)] px-8 py-5 flex items-center justify-between bg-[var(--bg)] sticky top-0 z-10">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[var(--text-3)]">
              {loc.pathname.split("/").filter(Boolean).join(" / ") || "dashboard"}
            </div>
            <div className="text-2xl font-semibold tracking-tight" data-testid="page-title">
              {pageTitleFor(loc.pathname)}
            </div>
          </div>
          <div className="text-xs text-[var(--text-3)] font-mono">v1.0.3</div>
        </header>
        <div className="p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

function pageTitleFor(path) {
  const m = {
    "/dashboard": "Overview",
    "/operators": "Operators",
    "/providers": "Providers",
    "/games": "Game Catalog",
    "/operator-credits": "Operator Credits",
    "/wallet": "Wallet Ledger",
    "/sessions": "Sessions",
    "/sandbox": "Sandbox Testing Center",
    "/reconciliation": "Reconciliation Center",
    "/settlement": "Settlement Center",
    "/webhooks": "Webhook Logs",
    "/monitoring": "Monitoring Center",
    "/audit": "Audit Center",
    "/ekyc": "eKYC Verifications",
    "/media": "Media Library",
  };
  for (const k of Object.keys(m)) if (path.startsWith(k)) return m[k];
  return "Aggregator";
}
